// eslint:disable max-func-body-length
import React, { useState, useEffect } from 'react';
import { DateTime, MeatballMenu, SectionPanel } from 'ui';
import { SelectMultiple, SelectSingle } from 'app/area/shared/translated-components';
import { MainGridContainer } from 'ui/common/main-grid-container/main-grid-container';
import { parseAsDateUTC, createUTCDate, dateToUTC, createFormatters, isDisabled } from 'app/infrastructure/data/date';
import { MetroLineSection } from 'app/components/metro-line/metro-line-section';
import { FavouriteSectionType } from '../metroline/types';
import { FavouriteFormIds } from '../constants';
import { FavouriteSection } from '../types';
import { ComplexValue, LoadOptions } from 'app/types-shared';
import { endpoints } from '../endpoints';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { maxFavoriteIdForOwner } from 'app/area/favourite-administration/constants';
import {
  disabledValidation, disabledWarning, favouriteLinkWithParams, favouriteTypeReadOnly,
  isOwnerReadOnly, ownerValidation, parentValidation
} from 'app/area/favourite-administration/form/components/helpers';
import { LinkField } from 'ui/common/link-field/link-field';
import { getFieldId } from 'app/area/search-filters/filters/selectors';
import { companyReportType, fieldType, filterId } from 'app/area/search-filters/constants';
import styles from './favourite-details.scss';

export const FavouriteDetails: React.FC<FavouriteSection> = ({
  favouriteItem, originalFavouriteItem, options, updateItem, locale, validation, translation, section,
  loadOptions, getRef, shouldRenderSection, clearParent, hideRemoveSchedule
}) => {

  const onDisabledChange = (value: Date) => {
    updateItem({ disabled: value });
  };

  const onOwnerChange = (owners: ComplexValue<number>[]) => {
    updateItem({ owners });
  };

  const onPrimaryOwnerChange = (primaryOwner: ComplexValue<number>) => {
    updateItem({ primaryOwner });
  };

  useEffect(() => {
    if (hideRemoveSchedule) {
      updateItem({ primaryOwner: undefined, hasSchedule: false });
    }
  }, [hideRemoveSchedule]);

  const [primaryOwnerQuery, setPrimaryOwnerQuery] = useState('');

  const onPrimaryOwnerLoadOptions = (params: LoadOptions) => {
    setPrimaryOwnerQuery(params?.query || '');
  };

  const primaryOwnerOptions = (favouriteItem?.owners || []).filter(owner =>
    !primaryOwnerQuery || owner.description?.toLowerCase().includes(primaryOwnerQuery.toLowerCase())
  );

  const onOwnerLoadOptions = (loadOptionsParams: LoadOptions) => {
    loadOptions(
      FavouriteFormIds.owners,
      endpoints.ownerOptions,
      loadOptionsParams,
      { ...(!!favouriteItem?.parent?.value && { favouriteId: favouriteItem?.id }) }
    );
  };

  const onReportTypeChange = (favouriteType: ComplexValue<number>) => {
    updateItem({ favouriteType });
  };

  const onFavouriteTypeLoadOptions = (loadOptionsParams: LoadOptions) => {
    loadOptions(FavouriteFormIds.reportType, endpoints.favouriteTypeOptions, loadOptionsParams);
  };

  const isFavouriteTypeReadOnly = favouriteTypeReadOnly(originalFavouriteItem?.favouriteType?.value);
  const isDisabledParent = isDisabled(favouriteItem?.parent?.disabled);
  const primaryOwnerRequired = favouriteItem?.hasSchedule && !hideRemoveSchedule;

  const owners = <SelectMultiple
    componentId={FavouriteFormIds.owners}
    className={undefined}
    locale={locale}
    valueItems={favouriteItem?.owners || []}
    label={{ text: translation.owner, visible: true, required: favouriteItem?.id >= maxFavoriteIdForOwner }}
    readOnly={isOwnerReadOnly(favouriteItem?.id)}
    options={options?.[FavouriteFormIds.owners]?.codes as ComplexValue<number>[] || []}
    placeholder={translation?.search}
    validation={
      ownerValidation(
        favouriteItem,
        originalFavouriteItem,
        validation?.[FavouriteFormIds.owners],
        translation
      )
    }
    fetching={options && options[FavouriteFormIds.owners]?.fetching}
    onLoadOptions={onOwnerLoadOptions}
    onChange={onOwnerChange}
    displaySelectAll={true} />;

  return <MetroLineSection<FavouriteSectionType>
    section={section}
    sectionType={FavouriteSectionType.Details}
    getRef={getRef}
    shouldRenderSection={shouldRenderSection}>
    <div ref={getRef(FavouriteSectionType.Details)} className={styles.favouriteDetails}>
      <SectionPanel
        className="admin-paper-top-panel"
        componentId={FavouriteSectionType.Details}
        validation={undefined}
        info={{ text: translation.details }}
        label={{ visible: true, text: translation.details }} />
      <MainGridContainer>
        <div className="row">
          <div className="col-lg-6 col-sm-12">
            <LinkField
              componentId={FavouriteFormIds.parent}
              label={translation.parent}
              text={favouriteItem?.parent?.description}
              linkUrl={favouriteItem?.parent
                && favouriteLinkWithParams(getFieldId(filterId.favouriteId, fieldType.root), favouriteItem?.ancestors)}
              validation={parentValidation(favouriteItem, validation?.[FavouriteFormIds.parent], translation)}>
              {favouriteItem?.parent
                && <MeatballMenu className={styles.parentMeatball}>
                  <li>
                    <button type="button" className="dropdown-item" onClick={clearParent}>
                      {translation.clear_parent}
                    </button>
                  </li>
                </MeatballMenu>}
            </LinkField>
          </div>
          <div className="col-lg-6 col-sm-12">
            <DateTime
              componentId={FavouriteFormIds.disabledId}
              className="dateTime"
              value={favouriteItem?.disabled}
              label={{ text: translation.disabled_removed }}
              info={{ text: translation.disabled_tooltip, visible: true }}
              readOnly={isDisabledParent}
              useTime={false}
              parseDate={parseAsDateUTC}
              createDate={createUTCDate}
              cloneDate={dateToUTC}
              {...createFormatters(locale)}
              locale={locale}
              onChange={onDisabledChange}
              captionWrapper={'form'}
              validation={disabledValidation(
                isDisabledParent,
                disabledWarning(favouriteItem?.favouriteType?.value, favouriteItem?.disabled),
                validation?.[FavouriteFormIds.disabledId],
                translation)} />
          </div>
        </div>
        <div className="row">
          <div className="col-lg-6 col-sm-12">
            <SelectSingle
              componentId={FavouriteFormIds.favouriteType}
              className={undefined}
              valueItem={isDefinedIncludingZero(favouriteItem?.favouriteType?.value) && favouriteItem.favouriteType}
              label={{ text: translation.favourite_type, visible: true, required: !isFavouriteTypeReadOnly }}
              readOnly={isFavouriteTypeReadOnly}
              options={options?.[FavouriteFormIds.reportType]?.codes as ComplexValue<number>[] || []}
              placeholder={translation?.search}
              validation={validation?.[FavouriteFormIds.favouriteType]}
              fetching={options?.[FavouriteFormIds.reportType]?.fetching}
              onLoadOptions={onFavouriteTypeLoadOptions}
              onChange={onReportTypeChange} />
          </div>
          <div className="col-lg-6 col-sm-12">
            {favouriteItem?.reportType?.value !== companyReportType
              ? owners
              : <div className={styles.withPrimaryOwner}>
                {owners}
                {primaryOwnerRequired &&
                  <SelectSingle
                    componentId={FavouriteFormIds.primaryOwner}
                    className={undefined}
                    valueItem={favouriteItem?.primaryOwner}
                    label={{ text: translation.primary_owner, required: primaryOwnerRequired, visible: true }}
                    info={{ visible: true, text: translation.primary_owner_tooltip_message }}
                    readOnly={isOwnerReadOnly(favouriteItem?.id)}
                    options={primaryOwnerOptions}
                    placeholder={translation?.search}
                    validation={validation?.[FavouriteFormIds.primaryOwner]}
                    onLoadOptions={onPrimaryOwnerLoadOptions}
                    onChange={onPrimaryOwnerChange} />}
              </div>}
          </div>
        </div>
      </MainGridContainer>
    </div>
  </MetroLineSection>;
};
